import { useState, type FC } from 'react';
//Tecton Components
import { Empty, TableViewLayout } from '@lithos';
import { Badge, Button, ExtendedTableColumn, FlexGroup, FlexItem, Input, Table, Link } from '@lithos';
import { useGetComputeIdentities } from './compute-utils';

interface ComputeIdentity {
  name: string;
  applicationId: string;
  workspace: string;
  isDefault: boolean;
}

const SearchInput = ({
  searchValue,
  setSearchValue,
}: {
  searchValue: string;
  setSearchValue: (value: string) => void;
}) => {
  return (
    <Input
      type="string"
      placeholder={'Search'}
      value={searchValue}
      onChange={(value) => {
        setSearchValue(value);
      }}
    />
  );
};

const ComputeIdentitiesContainer: FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const identities = useGetComputeIdentities();

  const columns: ExtendedTableColumn<ComputeIdentity & { id: string }>[] = [
    {
      id: 'id',
      name: 'ID',
      content: (item) => {
        return (
          <FlexGroup>
            <FlexItem grow="0">{item.applicationId}</FlexItem>
            <FlexItem>{item.isDefault && <Badge label="Default" size="s" color="gray" />}</FlexItem>
          </FlexGroup>
        );
      },
    },
    {
      id: 'workspace',
      name: 'Workspaces',
      content: (item) => {
        return (
          <>
            <Link icon="ProductionWorkspace" name={item.workspace} />
          </>
        );
      },
    },
  ];

  const items = searchQuery
    ? identities?.filter((identity) => {
        const includesText = `${identity.name}${identity.applicationId}${identity.workspace}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        return includesText;
      })
    : identities;

  const filteredItems = items.filter((item) => {
    return `${item.name}${item.applicationId}${item.workspace}`.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <TableViewLayout
      searchInput={<SearchInput searchValue={searchQuery} setSearchValue={setSearchQuery} />}
      filterAndColumnControls={undefined}
      table={
        filteredItems.length > 0 ? (
          <Table items={filteredItems.map((item) => ({ ...item, id: item.name }))} columns={columns} />
        ) : (
          <Empty
            placement={'full-page'}
            actions={
              searchQuery ? (
                <FlexGroup justifyContent="center">
                  <Button onClick={() => setSearchQuery('')} type="primary" label="Clear Search" />
                </FlexGroup>
              ) : undefined
            }
            useCase={'empty'}
            title={'No Matching Compute Identities'}
            description={
              items.length === 0 ? 'No compute identities found.' : 'No compute identities match this search term.'
            }
          />
        )
      }
    />
  );
};

export default ComputeIdentitiesContainer;
